import { useStore } from '../store/StoreContext.jsx';
import { genUid } from '../store/uid.js';
import { emoneyColumnKeys } from '../store/emoneyRequired.js';
import TableCell from './TableCell.jsx';
import ViewButton from './ViewButton.jsx';
import { Fragment, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

// Column-priority breakpoint (R2): when the table's container gives its
// visible columns less than this many px each, columns marked `detail: true`
// leave the row for a per-row expandable panel. Raised from 96 → 112 (UAT:
// Eugene, 7/9) — at 96px, wide tables (the investment accounts, Real Estate)
// stayed inline and crunched a seven-figure market value to "$1,00…" on a
// laptop at 100%. Demoting a touch sooner keeps identity + money readable.
const COMPACT_PX_PER_COL = 112;
// A table may instead declare its own one-line minimum (tableDef.inlineMinPx):
// the container width below which its full column set no longer fits and it
// should compact. Used where every column carries a pixel hint (Real Estate),
// so "fits" is arithmetic rather than a per-column guess. The per-column rule
// above stays for tables sized by percentages.

function formatCommas(n) {
  if (n == null || isNaN(n)) return '';
  return n % 1 === 0
    ? n.toLocaleString('en-US')
    : n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function sumColumn(rows, colKey) {
  return rows.reduce((sum, r) => {
    const raw = r?.[colKey];
    if (raw == null || raw === '') return sum;
    const n = parseFloat(String(raw).replace(/,/g, ''));
    return isNaN(n) ? sum : sum + n;
  }, 0);
}

// Pure array move used by drag-and-drop / keyboard row reordering. Returns a new
// array with the item at `from` reinserted at `to`; a no-op (same ref semantics
// aside) for out-of-range or identical indices so callers can skip the update.
export function moveItem(arr, from, to) {
  if (!Array.isArray(arr)) return arr;
  if (from === to || from < 0 || to < 0 || from >= arr.length || to >= arr.length) return arr;
  const next = arr.slice();
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);
  return next;
}

export default function DataTable({ sectionId, tableDef, noSeed = false, onRemoveRow }) {
  const { data, update, deleteRow, readOnly } = useStore();
  const path = `${sectionId}.${tableDef.key}`;
  // Columns eMoney needs on every populated row — a blue dot on the header
  // flags them (FYI only; the Plan Order screen does the actual gating).
  const emoneyCols = emoneyColumnKeys(path);
  const stored = data?.[sectionId]?.[tableDef.key];
  const rows = Array.isArray(stored) ? stored.filter(r => r != null) : null;

  // Seed rows on first render if storage is empty.
  // Skipped when the parent manages rows externally (noSeed = true).
  useEffect(() => {
    if (noSeed) return;
    if (rows && rows.length > 0) return;
    const seed = (tableDef.starterRows && tableDef.starterRows.length)
      // Tag the app-provided fields (_seeded) so completion doesn't count a
      // starter row's defaults (e.g. Relationships roles) as user progress.
      ? tableDef.starterRows.map(r => ({ ...r, _uid: genUid(), _seeded: Object.keys(r) }))
      : [{ _uid: genUid() }];
    update(path, seed);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const current = rows && rows.length > 0 ? rows : [];

  const setCell = (rowIdx, colKey, value) => {
    const next = current.map((r, i) => i === rowIdx ? { ...r, [colKey]: value } : r);
    update(path, next);
  };

  // Delete via the store so the removal is undoable for a few seconds (R4).
  // Pass the row's identity as the toast label when it has one.
  const removeRow = (rowIdx, row) => {
    const id = String(row?.[firstVisibleKey] ?? '').trim();
    deleteRow(path, rowIdx, row, { noSeed, label: id || null });
    // Let the section react to the removal — e.g. a Real Estate row created by a
    // checkbox unchecks that checkbox, so the reconcile effect doesn't just
    // re-add the row the user is trying to delete (John, 7/10).
    onRemoveRow?.(row);
  };

  const showTotals = !!tableDef.showTotals;

  // Pin the first visible column so a row's identity (Description / Custodian /
  // Owner) stays in view while the wide tables scroll horizontally (U4).
  const firstVisibleKey = (tableDef.columns.find(c => !c.hidden) || {}).key;

  // Accessible name for the table and its controls. Title-less tables (the
  // ones headed by a .subhead outside this component) declare an ariaTitle.
  const tableName = tableDef.title || tableDef.ariaTitle || '';
  // A row is announced by its first visible column's value ("Primary
  // Residence"); unnamed rows fall back to their position.
  const rowIdentity = (row, rowIdx) => {
    const id = String(row?.[firstVisibleKey] ?? '').trim();
    return id || `row ${rowIdx + 1}`;
  };
  const colClass = col => {
    const right = col.type === 'currency' || col.type === 'percent' || col.type === 'number';
    // Width hint: an explicit colClass on the column wins; otherwise derive a
    // sensible default from the type so short numerics (rates, ages, %) don't
    // claim an equal share. Unhinted columns split the remaining width equally
    // under table-layout:fixed.
    const width = col.colClass
      || (col.type === 'percent' || col.type === 'number' || col.type === 'checkbox' ? 'col-narrow'
         : col.type === 'select' || col.type === 'datalist' ? 'col-medium'
         : undefined);
    // `money` tags currency columns so a few-column table (.tbl-tight) can pin
    // them to a readable width instead of letting a lone value stretch across
    // the row. Wide tables ignore it and keep currency columns fluid.
    return [
      right ? 'right' : '',
      col.type === 'currency' ? 'money' : '',
      col.key === firstVisibleKey ? 'sticky-col' : '',
      width,
    ].filter(Boolean).join(' ') || undefined;
  };

  // --- Column priority (R2) ---
  // Columns flagged `detail: true` are demoted out of the row when the table's
  // container can't give every column useful width (the 1280-1366px laptop
  // case). The primary row keeps identity + money; the demoted fields render in
  // a per-row panel behind a disclosure, bound to the same store paths, so
  // Salesforce semantics and validation are untouched. On a wide monitor the
  // same columns simply stay inline.
  const wrapRef = useRef(null);
  // Browser zoom baseline. Zooming out widens the CSS container (clientWidth
  // grows) without giving columns more PHYSICAL width, so a fixed CSS threshold
  // stops compacting and 7-figure values crush at, say, 90% zoom. devicePixelRatio
  // tracks zoom; base/current cancels the display's own scaling, leaving just the
  // zoom factor. Captured once at mount.
  const baseDprRef = useRef(typeof window !== 'undefined' ? (window.devicePixelRatio || 1) : 1);
  const visibleCols = tableDef.columns.filter(c => !c.hidden);
  const detailCols = visibleCols.filter(c => c.detail);
  const hasDetail = detailCols.length > 0;
  const [compact, setCompact] = useState(false);
  // Which rows have their detail panel open, keyed by the row's stable key.
  const [openRows, setOpenRows] = useState(() => new Set());
  // Which row's note popover is open (row key, or null). One at a time.
  // The popover is rendered in a BODY PORTAL with fixed positioning, the same
  // approach the Combobox menu uses: the table wrapper is overflow-x:auto, so
  // an absolutely-positioned panel inside a cell gets clipped (it appeared
  // half-cut with the Done button unreachable, and forced a scrollbar).
  const [noteOpenKey, setNoteOpenKey] = useState(null);
  const [noteRect, setNoteRect] = useState(null);
  const noteBtnRef = useRef(null);
  const notePopRef = useRef(null);

  const NOTE_POP_W = 280;
  const NOTE_POP_H = 160; // approx: 4-row textarea + Done button
  const placeNote = useCallback(el => {
    if (!el) return;
    const r = el.getBoundingClientRect();
    // Keep the whole panel on screen: left-align to the icon but clamp
    // horizontally, and flip above the icon when a row near the bottom of the
    // viewport wouldn't leave room below (otherwise the Done button lands off
    // screen, which is what made the first version unusable).
    const left = Math.max(8, Math.min(r.left, window.innerWidth - NOTE_POP_W - 8));
    const below = r.bottom + 6;
    const top = below + NOTE_POP_H > window.innerHeight
      ? Math.max(8, r.top - NOTE_POP_H - 6)
      : below;
    setNoteRect({ top, left });
  }, []);

  const openNote = useCallback((key, el) => {
    noteBtnRef.current = el;
    placeNote(el);
    setNoteOpenKey(key);
  }, [placeNote]);

  const closeNote = useCallback(() => {
    setNoteOpenKey(null);
    noteBtnRef.current = null;
  }, []);

  // Follow the anchor while open (the table scrolls horizontally, page vertically).
  useEffect(() => {
    if (!noteOpenKey) return undefined;
    const onScroll = () => placeNote(noteBtnRef.current);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
  }, [noteOpenKey, placeNote]);

  // Click anywhere outside the icon or the panel closes it (the Done button
  // stays, but advisors shouldn't have to hunt for it).
  useEffect(() => {
    if (!noteOpenKey) return undefined;
    const onDocDown = e => {
      if (noteBtnRef.current?.contains(e.target)) return;
      if (notePopRef.current?.contains(e.target)) return;
      closeNote();
    };
    document.addEventListener('mousedown', onDocDown);
    return () => document.removeEventListener('mousedown', onDocDown);
  }, [noteOpenKey, closeNote]);

  useLayoutEffect(() => {
    if (!hasDetail) return undefined;
    const el = wrapRef.current;
    if (!el) return undefined;
    const check = () => {
      // Scale the per-column floor by the zoom factor so compaction tracks
      // physical width: zoomed out (current dpr < base) → higher threshold →
      // stays compacted; zoomed in naturally narrows the container already.
      const dpr = window.devicePixelRatio || 1;
      const base = tableDef.inlineMinPx || visibleCols.length * COMPACT_PX_PER_COL;
      const threshold = base * (baseDprRef.current / dpr);
      setCompact(el.clientWidth < threshold);
    };
    check();
    // The observer reacts to container-only changes (panel collapse, Focus) at a
    // constant window size; the resize listener is a fallback for throttled
    // observer delivery; the media query catches a zoom change that alters
    // devicePixelRatio without a resize.
    const ro = new ResizeObserver(check);
    ro.observe(el);
    window.addEventListener('resize', check);
    let mq = window.matchMedia(`(resolution: ${window.devicePixelRatio || 1}dppx)`);
    const onZoom = () => {
      check();
      mq.removeEventListener('change', onZoom);
      mq = window.matchMedia(`(resolution: ${window.devicePixelRatio || 1}dppx)`);
      mq.addEventListener('change', onZoom);
    };
    mq.addEventListener('change', onZoom);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', check);
      mq.removeEventListener('change', onZoom);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasDetail, visibleCols.length]);

  // Hidden columns always render (their type=hidden inputs carry no width);
  // detail columns leave the row only while compact.
  const shownCols = compact
    ? tableDef.columns.filter(c => c.hidden || !c.detail)
    : tableDef.columns;
  const renderedCols = shownCols.filter(c => !c.hidden);

  // Sub-row mode (tableDef.detailSubRow): when compact, the demoted fields
  // render as an ALWAYS-VISIBLE labeled second line under each row instead of
  // behind a per-row disclosure. Built for Real Estate, the app's widest table
  // and the one advisors run at half width during live client meetings: fields
  // hidden behind the chevron were reported as "hidden from view" (David
  // Kleinman 8/13), and stacked layouts that drop their labels after the first
  // row were the stated reason compressed views became unreadable. A labeled
  // line per row answers both, and costs only height — which the form has.
  const subRowMode = compact && !!tableDef.detailSubRow;

  const rowKey = (row, rowIdx) =>
    row._uid || row._autoKey || row._source || row._reKey || rowIdx;

  // --- Row reordering (opt-in via tableDef.reorderable) ---
  // Only enabled on purely-manual tables: an auto-source-synced table (Income,
  // Expenses, Liabilities) rebuilds its own row order every tick, so a manual
  // reorder there wouldn't stick. Drag by the grip handle (so cell inputs stay
  // editable); Alt+ArrowUp/Down moves the focused row for keyboard users.
  const reorderable = !!tableDef.reorderable;
  const [dragIdx, setDragIdx] = useState(null);
  const [overIdx, setOverIdx] = useState(null);
  // A handle to refocus after a keyboard move, so repeated Alt+Arrow keeps
  // working as the row (and its DOM node) moves.
  const pendingHandleFocus = useRef(null);

  useEffect(() => {
    const to = pendingHandleFocus.current;
    if (to == null) return;
    const el = wrapRef.current?.querySelector(`button.row-drag[data-handle-row="${to}"]`);
    if (el) { el.focus(); pendingHandleFocus.current = null; }
  });

  const commitMove = (from, to) => {
    if (to < 0 || to >= current.length) return;
    const next = moveItem(current, from, to);
    if (next !== current) update(path, next);
  };

  const onDragStart = (e, idx) => {
    setDragIdx(idx);
    e.dataTransfer.effectAllowed = 'move';
    // Some browsers require data to be set for a drag to initiate.
    try { e.dataTransfer.setData('text/plain', String(idx)); } catch { /* ignore */ }
  };
  const onRowDragOver = (e, idx) => {
    if (dragIdx == null) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (idx !== overIdx) setOverIdx(idx);
  };
  const onRowDrop = (e, idx) => {
    if (dragIdx == null) return;
    e.preventDefault();
    commitMove(dragIdx, idx);
    setDragIdx(null);
    setOverIdx(null);
  };
  const onDragEnd = () => { setDragIdx(null); setOverIdx(null); };

  const onHandleKey = (e, idx) => {
    if (!e.altKey) return;
    const dir = e.key === 'ArrowUp' ? -1 : e.key === 'ArrowDown' ? 1 : 0;
    if (!dir) return;
    e.preventDefault();
    const to = idx + dir;
    if (to < 0 || to >= current.length) return;
    commitMove(idx, to);
    pendingHandleFocus.current = to; // follow the row to its new position
  };

  const toggleDetail = key =>
    setOpenRows(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  // Flag the disclosure when the collapsed fields hold data, so a record
  // entered on a wide monitor doesn't silently hide values on a laptop.
  const rowHasDetailData = row =>
    detailCols.some(c => String(row?.[c.key] ?? '').trim() !== '');

  // --- Keyboard-first row entry (R1) ---
  // The table behaves like a spreadsheet: Enter moves straight down the same
  // column between existing rows, Esc reverts the current cell. Enter off the
  // last row adds a new one and starts it at its FIRST cell (finish a row,
  // start typing the next from the beginning). All coordinated here via
  // data-row/data-col on each cell so TableCell's many input types don't each
  // need wiring.
  // Value captured when a cell gains focus, so Esc can revert that cell's edit.
  const editStart = useRef(null);
  // A cell to focus after the next render, set when a row is added and consumed
  // by the effect below once the new row's input exists in the DOM.
  const pendingFocus = useRef(null);

  // Not `td[...]`: in sub-row mode the demoted fields live in labelled wrappers
  // inside one spanning cell, and they carry the same data-row/data-col so
  // Enter-moves-down works identically in both layouts.
  const focusCell = (rowIdx, colKey) => {
    const cell = wrapRef.current?.querySelector(
      `[data-row="${rowIdx}"][data-col="${colKey}"]`
    );
    const el = cell?.querySelector('input:not([type="hidden"]), select, textarea');
    if (el) { el.focus(); return true; }
    return false;
  };

  useEffect(() => {
    const p = pendingFocus.current;
    if (p && focusCell(p.rowIdx, p.colKey)) pendingFocus.current = null;
  });

  // Append a blank row and queue focus onto its first column — a fresh row
  // starts at the beginning whether it came from Enter or the Add button.
  const addRow = () => {
    pendingFocus.current = { rowIdx: current.length, colKey: firstVisibleKey };
    update(path, [...current, { _uid: genUid() }]);
  };

  const onCellFocus = e => {
    const cell = e.target.closest?.('[data-col]');
    if (!cell) return;
    editStart.current = {
      rowIdx: Number(cell.dataset.row),
      colKey: cell.dataset.col,
      value: current[Number(cell.dataset.row)]?.[cell.dataset.col],
    };
  };

  const onCellKeyDown = e => {
    const cell = e.target.closest?.('[data-col]');
    if (!cell) return;
    const rowIdx = Number(cell.dataset.row);
    const colKey = cell.dataset.col;

    if (e.key === 'Enter') {
      // The Combobox stops propagation when it commits a highlighted option, so
      // if Enter reached here it means "move down" (spreadsheet muscle memory).
      e.preventDefault();
      if (rowIdx >= current.length - 1) addRow();
      else focusCell(rowIdx + 1, colKey);
    } else if (e.key === 'Escape') {
      const s = editStart.current;
      if (s && s.rowIdx === rowIdx && s.colKey === colKey) {
        setCell(rowIdx, colKey, s.value ?? '');
        // Blur so an input with a local edit buffer (CurrencyInput) drops it and
        // shows the reverted value; fully-controlled inputs revert on their own.
        e.target.blur?.();
      }
    }
  };

  return (
    <div>
      {/* Toolbar sits above every table (even title-less ones) so the compact
          Add button lives in the top-right corner instead of a full-width
          button below the table — saves vertical space and scrolling. */}
      <div className="tbl-tools">
        <div className={'tbl-title' + (tableDef.titleClass ? ` ${tableDef.titleClass}` : '')}>
          {tableDef.titleClass && <span className="dot" aria-hidden="true" />}
          {tableDef.title}
        </div>
        {/* Optional control beside the title, e.g. the investment sections'
            "None" marker. Rendered here rather than by the caller so it sits in
            the toolbar with the title instead of above a second header. */}
        {tableDef.headerAction}
        <button
          type="button"
          className="add-row"
          onClick={() => addRow()}
          aria-label={tableName ? `Add row to ${tableName}` : 'Add row'}
        >
          <span className="add-row-icon" aria-hidden="true">+</span>
          <span>Add</span>
        </button>
      </div>
      <div className="tbl-wrap" ref={wrapRef} onKeyDown={onCellKeyDown} onFocus={onCellFocus}>
        <table
          // Tight = pin the money column to a readable width and let the
          // identity column absorb the slack. It exists for one shape: a few
          // columns with a LONE money column, where a fluid Market Value would
          // otherwise span the row (Cash & CD's, Business & Other, a compacted
          // investment table). Two things it must never do, both learned on
          // 2026-08-17: count DEFINED rather than rendered columns (a wide
          // table compacted to three was "not tight" and its Market Value
          // ballooned), and apply to a table with SEVERAL money columns
          // (Real Estate has three; pinning all of them starved Description
          // to nothing at first, then swelled it to half the row). So: few
          // rendered columns AND exactly one rendered money column.
          className={
            'tbl' +
            (renderedCols.length <= 6 && renderedCols.filter(c => c.type === 'currency').length === 1
              ? ' tbl-tight'
              : '')
          }
          aria-label={tableName || undefined}
        >
          <thead>
            <tr>
              {reorderable && <th className="col-drag" aria-label="Reorder" />}
              {/* Note lives at the LEFT edge: it is a row-level control like the
                  drag handle, it is far more visible here, and it is no longer
                  adjacent to the delete X (advisors were at risk of hitting the
                  wrong one). */}
              <th className="col-note" aria-label="Note" />
              {shownCols.map(c => (
                <th
                  key={c.key}
                  className={colClass(c)}
                  style={c.hidden ? { display: 'none' } : undefined}
                >
                  {c.label}
                  {emoneyCols.has(c.key) && (
                    <span
                      className="emoney-dot"
                      role="img"
                      tabIndex={0}
                      aria-label="Needed for eMoney, will be required on Plan Order submission."
                      title="Needed for eMoney, will be required on Plan Order submission."
                      style={{ marginLeft: 5, verticalAlign: 'middle' }}
                    />
                  )}
                  {c.labelInfo && (
                    <span
                      tabIndex={0}
                      title={c.labelInfo}
                      aria-label={c.labelInfo}
                      style={{
                        marginLeft: 4,
                        display: 'inline-grid',
                        placeItems: 'center',
                        width: 14,
                        height: 14,
                        borderRadius: '50%',
                        background: 'var(--surface-sunk)',
                        color: 'var(--ink-4)',
                        fontSize: 9,
                        cursor: 'help',
                      }}
                    >
                      i
                    </span>
                  )}
                </th>
              ))}
              {compact && !subRowMode && <th className="col-toggle" aria-label="Details" />}
              <th className="col-x" aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {current.map((row, rowIdx) => {
              const key = rowKey(row, rowIdx);
              const open = compact && openRows.has(key);
              // Auto-sourced rows (an expense mirrored from a liability/insurance/
              // rent, a derived income or mortgage row) carry a _readOnly list.
              // They regenerate from their source, so a direct delete can't stick
              // (UAT: Gina deleted an insurance-driven expense and it came back).
              // Show a lock instead of the × and point the advisor at the source.
              const autoManaged = Array.isArray(row._readOnly) && row._readOnly.length > 0;
              return (
                <Fragment key={key}>
                  <tr
                    className={
                      (dragIdx === rowIdx ? 'is-dragging' : '') +
                      (overIdx === rowIdx && dragIdx !== rowIdx ? ' drag-over' : '') +
                      (subRowMode ? ' has-sub' : '')
                    }
                    onDragOver={reorderable ? e => onRowDragOver(e, rowIdx) : undefined}
                    onDrop={reorderable ? e => onRowDrop(e, rowIdx) : undefined}
                  >
                    {reorderable && (
                      <td className="col-drag">
                        <button
                          type="button"
                          className="row-drag"
                          draggable
                          data-handle-row={rowIdx}
                          aria-label={`Reorder ${rowIdentity(row, rowIdx)}. Drag, or hold Alt and press Arrow Up or Down.`}
                          title="Drag to reorder (or Alt+↑/↓)"
                          onDragStart={e => onDragStart(e, rowIdx)}
                          onDragEnd={onDragEnd}
                          onKeyDown={e => onHandleKey(e, rowIdx)}
                        >
                          <span aria-hidden="true">⠿</span>
                        </button>
                      </td>
                    )}
                    {/* Per-row note (Jake, 2026-07-23). Advisors need somewhere to
                        record context with no structured field: "son living here",
                        "two deposits, $300k in 2024", "asked, they didn't know".
                        Without it they type it into Description, which becomes a
                        junk account in eMoney. At the LEFT edge so it is visible
                        and not adjacent to the delete X. Panel is portalled to
                        body with fixed positioning so the overflow-x:auto table
                        wrapper cannot clip it. */}
                    <td className="col-note">
                      {/* A viewer can open a note that exists and has no use for
                          an empty one, so in view mode the icon only appears on
                          rows that carry a note. The cell stays either way so
                          the columns do not shift. */}
                      {(!readOnly || String(row.notes || '').trim()) && (
                      <ViewButton
                        className={'row-note' + (String(row.notes || '').trim() ? ' has-note' : '')}
                        title={String(row.notes || '').trim() || 'Add a note'}
                        aria-label={
                          String(row.notes || '').trim()
                            ? `${readOnly ? 'View' : 'Edit'} note for ${rowIdentity(row, rowIdx)}: ${row.notes}`
                            : `Add a note for ${rowIdentity(row, rowIdx)}`
                        }
                        aria-expanded={noteOpenKey === key}
                        onClick={e => (noteOpenKey === key ? closeNote() : openNote(key, e.currentTarget))}
                      >
                        <svg width="13" height="13" viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                        </svg>
                      </ViewButton>
                      )}
                      {noteOpenKey === key && noteRect && createPortal(
                        <div
                          ref={notePopRef}
                          className="row-note-pop"
                          style={{ top: noteRect.top, left: noteRect.left, width: NOTE_POP_W }}
                          role="dialog"
                          aria-label={`Note for ${rowIdentity(row, rowIdx)}`}
                        >
                          {/* This panel is portalled to <body>, outside the
                              disabled fieldset, so view mode has to be applied
                              here by hand or a viewer could type into it. */}
                          <textarea
                            className="row-note-input"
                            autoFocus
                            rows={4}
                            maxLength={1000}
                            placeholder="Add a note for this row…"
                            value={row.notes || ''}
                            readOnly={readOnly}
                            onChange={e => { if (!readOnly) setCell(rowIdx, 'notes', e.target.value); }}
                            onKeyDown={e => {
                              if (e.key === 'Escape') { e.stopPropagation(); closeNote(); }
                            }}
                          />
                          <div className="row-note-actions">
                            <button type="button" className="row-note-done" onClick={closeNote}>{readOnly ? 'Close' : 'Done'}</button>
                          </div>
                        </div>,
                        document.body
                      )}
                    </td>
                    {shownCols.map(col => (
                      <td
                        key={col.key}
                        data-row={rowIdx}
                        data-col={col.key}
                        className={colClass(col)}
                        style={col.hidden ? { display: 'none' } : undefined}
                      >
                        <TableCell
                          column={col}
                          value={row[col.key]}
                          row={row}
                          readOnly={Array.isArray(row._readOnly) && row._readOnly.includes(col.key)}
                          onChange={val => setCell(rowIdx, col.key, val)}
                          ariaLabel={`${col.label}, ${rowIdentity(row, rowIdx)}`}
                        />
                      </td>
                    ))}
                    {compact && !subRowMode && (
                      <td className="col-toggle">
                        <ViewButton
                          className={
                            'row-disclose' +
                            (open ? ' is-open' : '') +
                            (rowHasDetailData(row) ? ' has-data' : '')
                          }
                          aria-expanded={open}
                          aria-label={`${open ? 'Hide' : 'Show'} details for ${rowIdentity(row, rowIdx)}`}
                          onClick={() => toggleDetail(key)}
                        >
                          <span aria-hidden="true">▸</span>
                        </ViewButton>
                      </td>
                    )}
                    <td className="col-x">
                      {autoManaged ? (
                        <span
                          className="row-locked"
                          title="Added automatically from its source (e.g. an insurance premium or loan payment). Edit or remove that source to change this row."
                          aria-label={`${rowIdentity(row, rowIdx)} is added automatically from its source; edit the source to change it.`}
                        >
                          <svg width="11" height="11" viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="4.5" y="10.5" width="15" height="9.5" rx="2" />
                            <path d="M8 10.5V7a4 4 0 0 1 8 0v3.5" />
                          </svg>
                        </span>
                      ) : (
                        <button
                          type="button"
                          className="row-x"
                          aria-label={`Remove ${rowIdentity(row, rowIdx)}`}
                          onClick={() => removeRow(rowIdx, row)}
                        >
                          ×
                        </button>
                      )}
                    </td>
                  </tr>
                  {subRowMode && (
                    <tr className="row-sub">
                      {reorderable && <td className="col-drag" />}
                      <td className="col-note" />
                      <td
                        className="row-sub-td"
                        colSpan={shownCols.filter(c => !c.hidden).length + 1}
                      >
                        <div className="row-sub-flex">
                          {detailCols.map(col => (
                            <label
                              key={col.key}
                              className="row-sub-field"
                              data-row={rowIdx}
                              data-col={col.key}
                            >
                              <span className="row-sub-label">{col.label}</span>
                              <TableCell
                                column={col}
                                value={row[col.key]}
                                row={row}
                                readOnly={Array.isArray(row._readOnly) && row._readOnly.includes(col.key)}
                                onChange={val => setCell(rowIdx, col.key, val)}
                                ariaLabel={`${col.label}, ${rowIdentity(row, rowIdx)}`}
                              />
                            </label>
                          ))}
                        </div>
                      </td>
                    </tr>
                  )}
                  {open && (
                    <tr className="row-detail">
                      <td colSpan={shownCols.length + 3 + (reorderable ? 1 : 0)}>
                        <div className="row-detail-grid">
                          {detailCols.map(col => (
                            <label key={col.key} className="row-detail-field">
                              <span className="row-detail-label">
                                {col.label}
                                {col.labelInfo ? ` (${col.labelInfo.toLowerCase()})` : ''}
                              </span>
                              <TableCell
                                column={col}
                                value={row[col.key]}
                                row={row}
                                readOnly={Array.isArray(row._readOnly) && row._readOnly.includes(col.key)}
                                onChange={val => setCell(rowIdx, col.key, val)}
                                ariaLabel={`${col.label}, ${rowIdentity(row, rowIdx)}`}
                              />
                            </label>
                          ))}
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
          {showTotals && current.length > 0 && (
            <tfoot>
              <tr>
                {reorderable && <td className="col-drag" />}
                {/* Matches the note column now sitting at the left edge, so the
                    totals row stays aligned with the body rows. */}
                <td className="col-note" />
                {shownCols.map(col => {
                  if (col.hidden) return <td key={col.key} style={{ display: 'none' }} />;
                  const sticky = col.key === firstVisibleKey;
                  if (col.type === 'currency') {
                    return (
                      <td key={col.key} className={'right num' + (sticky ? ' sticky-col' : '')}>
                        {formatCommas(sumColumn(current, col.key))}
                      </td>
                    );
                  }
                  // Anchor the "Total" label in the pinned first column so it
                  // stays visible (and shows even when column 0 is hidden).
                  if (sticky) return <td key={col.key} className="sticky-col">Total</td>;
                  return <td key={col.key} />;
                })}
                {compact && !subRowMode && <td className="col-toggle" />}
                <td className="col-x" />
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}
